import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import {
  EntityServiceClient,
  ReferenceDto,
  ReferenceFilterDto,
  ReferenceServiceClient
} from '@sharedcommon/rest/data.restservice';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UserService } from '@sharedcommon/services/user.service';
import { User } from '@sharedcommon/model/user.model';
import { Constants } from '@sharedcommon/util/constants.util';
import { map, tap } from 'rxjs/operators';
import { EntityDto, EntityFilterDto, SubEntityServiceClient, UserDto, UserFilterDto, UserServiceClient } from '@sharedcommon/rest/identity.restservice';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { lastValueFrom, of } from 'rxjs';
import { URN } from '@sharedcommon/model/urn.model';
import { SampleDto } from '@sharedcommon/model/sample/sample.model';
import { SpeciesDto } from '@sharedcommon/model/sample/species.model';
import { AnalysisOriginDto } from '@sharedcommon/model/sample/analysis-origin.model';
import { SampleFarmDto } from '@sharedcommon/model/sample/sample-farm.model';
import { SupplierRestService } from '@sharedcommon/rest/organization/supplier.restservice';
import { NeqAdvancedReferencesSearchPopinComponent } from '@usercommon/components/advanced-references-search/advanced-references-search-popin.component';
import { MatDialog } from '@angular/material/dialog';
import { SupplierDto } from '@sharedcommon/model/organization/supplier.model';
import { SpeciesRestService } from '@sharedcommon/rest/sample/species.restservice';
import { AnalysisOriginRestService } from '@sharedcommon/rest/sample/analysis-origin.restservice';
import { AnalysisOriginFilterDto } from '@sharedcommon/model/sample/filter/analysis-origin-filter.model';
import { SpeciesFilterDto } from '@sharedcommon/model/sample/filter/species-filter.model';
import { SupplierFilterDto } from '@sharedcommon/model/organization/filter/supplier-filter.model';
import { FunctionUtils } from '@sharedcommon/util/functions.util';
import { BusinessUnitRestService } from '@sharedcommon/rest/organization/business-unit.restservice';
import { BusinessUnitDto } from '@sharedcommon/model/organization/business-unit.model';
import { BusinessUnitFilterDto } from '@sharedcommon/model/organization/filter/business-unit-filter.model';
import { Observable } from 'rxjs-compat';
import { CustomerDto } from '@sharedcommon/model/organization/customer.model';
import { SampleCustomerDto } from '@sharedcommon/model/sample/sample-customer.model';
import { CustomerRestService } from '@sharedcommon/rest/organization/customer.restservice';
import { CustomerFilterDto } from '@sharedcommon/model/organization/filter/customer-filter.model';
import { NeqSelectAutocompleteComponent } from '@sharedcommon/components/select-autocomplete/neq-select-autocomplete.component';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';

const MAX_LENGTH_FOR_CODE = 50;
const MAX_LENGTH_FOR_LABEL = 255;

class SelectionItem<T> {
  value: T;
  label: string;

  constructor(value: T, label: string) {
    this.value = value;
    this.label = label;
  }
}

class LinkInfoToEntityInfo {
  isThereLinkedBu: boolean;
  isThereLinkedCustomer: boolean;
}

@Component({
  selector: 'neq-samples-mandatory-properties',
  templateUrl: './samples-mandatory-properties.component.html'
})
export class NeqSampleMandatoryProperties implements OnInit, OnChanges {

  readonly ERROR_READ_ONLY = 'readonly';

  /**
   * Indicate if there is a linked business unit to the entity
   */
  @Input() isThereLinkedBu: boolean;
  @Input() isThereLinkedCustomer: boolean;
  @Input() mandatoryForm: FormGroup;
  @Input() notesForm: FormControl;
  @Input() sample: SampleDto;
  @Input() selectedReference: ReferenceDto;
  @Input() selectedSupplier: SupplierDto;
  @Input() selectedEntity: EntityDto;
  @Input() selectedBusinessUnit: BusinessUnitDto;
  @Input() selectedCustomer: CustomerDto;
  @Input() fillFormDone: boolean;
  /**
   * The business unit of the entity selected in the entity selection page
   * which appears after the login page
   */
  @Input() businessUnitFromMainEntity: BusinessUnitDto;
  @Input() customerFromMainEntity: CustomerDto;
  @Input() canSave: boolean;

  @Output() selectedSupplierChange = new EventEmitter<SupplierDto>();
  @Output() isThereLinkedBuChange = new EventEmitter<boolean>();
  @Output() isThereLinkedCustomerChange = new EventEmitter<boolean>();
  @Output() selectedBusinessUnitChange = new EventEmitter<BusinessUnitDto>();
  @Output() businessUnitFromMainEntityChange = new EventEmitter<BusinessUnitDto>();
  @Output() customerFromMainEntityChange = new EventEmitter<CustomerDto>();
  @Output() selectedReferenceChange = new EventEmitter<ReferenceDto>();
  @Output() selectedEntityChange = new EventEmitter<EntityDto>();
  @Output() selectedCustomerChange = new EventEmitter<CustomerDto>();
  @Output() fillFormDoneChange = new EventEmitter<boolean>();
  @Output() saveClick = new EventEmitter<void>();

  @ViewChild('customerSelector') customerSelector: NeqSelectAutocompleteComponent;

  user: User;
  entityLogo: SafeUrl;
  canUpdateEntity = false;
  public isReadOnly = true;

  /**
   * For preselecting customer select
   */
  sampleCustomerIdentifier: string;

  constructor(
    private readonly entityService: EntityServiceClient,
    private readonly subEntityService: SubEntityServiceClient,
    private readonly businessUnitService: BusinessUnitRestService,
    private readonly customerService: CustomerRestService,
    private readonly userRestService: UserServiceClient,
    private readonly referenceService: ReferenceServiceClient,
    private readonly speciesService: SpeciesRestService,
    private readonly anaylysisService: AnalysisOriginRestService,
    private readonly supplierService: SupplierRestService,
    private readonly userService: UserService,
    private readonly dialogMat: MatDialog,
    private readonly domSanitizer: DomSanitizer
  ) {
    this.user = this.userService.getUser();
    this.updateMainBusinessUnit();
    this.updateMainCustomer();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.sample?.currentValue) {
      if (this.canFormBeUpdated()) {
        // Fill the mandatory form only if the user has not touched this before
        // Used to keep the state of the tab when we get back to it
        this.fillMandatoryForm(this.sample);
        this.fillFormDone = true;
        this.fillFormDoneChange.next(true);
        if(this.sample.customer) {
          this.sampleCustomerIdentifier = this.sample.customer.urn.identifier;
        }
      }
    } else if (this.customerFromMainEntity) {
      this.onCustomerChange(NeqSampleMandatoryProperties.returnSelectionItem(this.customerFromMainEntity));
    }
  }

  ngOnInit(): void {
    if (this.canFormBeUpdated()) {
      // Set the default param of the form if not touched by the user
      this.onEntityChange(NeqSampleMandatoryProperties.returnSelectionItem(this.userService.getEntityDto()));
      const userDto: UserDto = new UserDto();
      userDto.email = this.user.email;
      // Set the family name as full name to get the right technician name
      // when send creation of sample
      userDto.familyName = this.user.full_name;
      userDto.givenName = "";
      this.mandatoryForm.get("requester").setValue(new SelectionItem<UserDto>(userDto, userDto.email))
    }
    // Ugly -> I do not know why but we need it in case we get back to the tab
    // And get the form DISPLAYED in the component <neq-select-autocomplete> present in
    // the html. Without it the component do not display the form value
    // I think the bug is from the component <neq-select-autocomplete> which do not
    // display the form value on the initialization
    setTimeout(() => {
      for(const ctrl of Object.values(this.mandatoryForm.controls)) {
        const value = ctrl.value;
        ctrl.setValue(value);
      }
    }, 250);
  }

  canFormBeUpdated(): boolean {
    return this.mandatoryForm?.pristine && !this.fillFormDone;
  }

  /**
   * Update the business unit linked to the selected entity by user
   */
  updateMainBusinessUnit(): void {
    if (!this.businessUnitFromMainEntity && this.userService.getEntityDto()?.customer?.identifier) {
      // Retrive the main BU only is not retrieved before AND if there is customer linked to the entity
      this.retrieveBusinessUnitLinkToEntity(this.userService.getEntityDto())
        .subscribe(linkedBu => {
          this.businessUnitFromMainEntity = linkedBu
          this.businessUnitFromMainEntityChange.next(linkedBu);
        });
    }
  }

  /**
   * Update the customer linked to the selected entity by user
   */
  updateMainCustomer(): void {
    if (!this.customerFromMainEntity && this.userService.getEntityDto()?.customer?.identifier) {
      // Retrive the main BU only is not retrieved before AND if there is customer linked to the entity
      this.retrieveCustomerLinkToEntity(this.userService.getEntityDto())
        .subscribe(linkedCustomer => {
          this.customerFromMainEntity = linkedCustomer
          this.customerFromMainEntityChange.next(linkedCustomer);
        });
    }
  }

  /**
   * Create an empty mandatory skeleton form
   *
   * @returns the form for mandatory datas
   */
  public static createEmptyMandatoryForm(): FormGroup {
    return new FormGroup({
      code: new FormControl(null, [
        Validators.required,
        Validators.pattern(Constants.REGEX_ALL_EXCEPT_SPACES_AND_GENERIC_CURRENCY_SYMBOL),
        Validators.maxLength(MAX_LENGTH_FOR_CODE)
      ]),
      label: new FormControl(null, [Validators.required, Validators.maxLength(MAX_LENGTH_FOR_LABEL)]),
      // In format SelectionItem<ReferenceDto>
      reference: new FormControl(null, Validators.required),
      validationDate: new FormControl(new Date(), Validators.required),
      // In format SelectionItem<EntityDto>
      entity: new FormControl(null, Validators.required),
      // In format SelectionItem<SpeciesDto>
      species: new FormControl(null, Validators.required),
      // In format SelectionItem<SupplierDto>
      supplier: new FormControl(null, Validators.required),
      requester: new FormControl(null, Validators.required),
      customer: new FormControl(null, Validators.required),
      farmLabel: new FormControl(null, Validators.required),
      // In format SelectionItem<AnalysisOriginDto>
      analysisOrigin: new FormControl(null, Validators.required),
      businessUnit: new FormControl(null, Validators.required),
      excludeFromTheStats: new FormControl(false, Validators.required)
    });
  }

  /**
   * Fill the form with the sample datas. It will retrieve the objects from API calls
   * and update the corresponding meta datas
   *
   * @param sample the sample that contains the data to fill in the form
   */
  private fillMandatoryForm(sample: SampleDto): void {
    this.mandatoryForm.reset();
    NeqSampleMandatoryProperties.fillStaticFormData(sample, this.mandatoryForm);
    this.updateReferenceForm(sample);
    this.updateEntityAndLinkedForm(sample);
    this.updateSupplierForm(sample);
  }

  /**
   * Fill the form with the sample datas. It will not retrive object from API calls
   *
   * @param sample        the sample that contains the data to fill in the form
   * @param mandatoryForm the mandatory form which has been previously created by the method {@link createEmptyMandatoryForm }
   */
  public static fillMandatoryForm(sample: SampleDto, mandatoryForm: FormGroup): void {
    mandatoryForm.reset();
    NeqSampleMandatoryProperties.fillStaticFormData(sample, mandatoryForm);
    // use selected item from URN because as we are in a static method
    // we cannot retrieve datas from injected services
    mandatoryForm.get("reference").setValue(this.returnSelectionItemFromUrn(sample.reference));
    mandatoryForm.get("entity").setValue(this.returnSelectionItemFromUrn(sample.entity));
    mandatoryForm.get("supplier").setValue(this.returnSelectionItemFromUrn(sample.supplierUrn));
    mandatoryForm.get("businessUnit").setValue(this.returnSelectionItemFromUrn(sample.originalBusinessUnit));
    mandatoryForm.get("customer").setValue(this.returnSelectionItemFromUrn(sample.customer?.urn))
  }

  /**
   * Fill the form with sample static datas. Static datas are datas which does not need
   * to retrieve object for API call
   *
   * @param sample        the sample which does contains datas
   * @param mandatoryForm the form to fill
   */
  private static fillStaticFormData(sample: SampleDto, mandatoryForm: FormGroup): void {
    mandatoryForm.reset();
    mandatoryForm.get("code").setValue(sample.code);
    mandatoryForm.get("label").setValue(sample.label);
    mandatoryForm.get("validationDate").setValue(FunctionUtils.convertToDate(sample.validationDate));
    mandatoryForm.get("species").setValue(NeqSampleMandatoryProperties.returnSelectionItem(sample.species, false));
    // Create dummy user which will give same user information store in sample than when we
    // enter in the form in case the user do not touch the requester choice
    const user: UserDto = new UserDto();
    user.email = sample.requester;
    user.givenName = sample.technicianName;
    user.familyName = '';
    mandatoryForm.get("requester").setValue(new SelectionItem<UserDto>(user, user.email));
    mandatoryForm.get("farmLabel").setValue(sample.farm?.label ?? null);
    mandatoryForm.get("analysisOrigin").setValue(NeqSampleMandatoryProperties.returnSelectionItem(sample.analysisOrigin, false));
    mandatoryForm.get("excludeFromTheStats").setValue(sample.excludeFromTheStats ?? false);
  }

  /**
   * Retrieve the reference by code and update the form
   *
   * @param sample the sample which contain the refrence code to retrieve
   */
  private updateReferenceForm(sample: SampleDto): void {
    if (this.selectedReference?.code && this.selectedReference.code === sample.reference.identifier) {
      // Check if the selected reference is equal to the one from the sample to
      // avoid to request a reference which has already been requested
      this.onReferenceChange(NeqSampleMandatoryProperties.returnSelectionItem(this.selectedReference));
      return;
    }
    this.referenceService.getByCode(sample.reference.identifier, { translated : true})
      .subscribe(ref => {
        this.onReferenceChange(NeqSampleMandatoryProperties.returnSelectionItem(ref));
      });
  }

  onReferenceChange(selectionItem: SelectionItem<ReferenceDto>): void {
    this.selectedReference = selectionItem?.value;
    this.mandatoryForm.get("reference").setValue(selectionItem);
    this.selectedReferenceChange.next(this.selectedReference);
  }

  /**
   * Retrieve the supplier by code and update the form
   *
   * @param sample the sample which contain the supplier code to retrieve
   */
  private updateSupplierForm(sample: SampleDto): void {
    if (this.selectedSupplier?.code && this.selectedSupplier.code === sample?.supplierUrn.identifier) {
      // Check if the selected supplier is equal to the one from the sample to
      // avoid to request a supplier which has already been retrieved
      this.onSupplierChange(NeqSampleMandatoryProperties.returnSelectionItem(this.selectedSupplier));
      return;
    }
    const supplierCodeFromSample = sample.supplierUrn.identifier;
    this.supplierService.getByCode(supplierCodeFromSample)
      .subscribe({
        next: supplier => this.onSupplierChange(NeqSampleMandatoryProperties.returnSelectionItem(supplier)),
        error: (error: HttpErrorResponse) => {
          if (error?.status === HttpStatusCode.NotFound) {
            // The reasons that the supplier code that appears in the sample does not exsits can be due to event
            // that happens after the creation or updates of sample like :
            // the supplier has been removed or the supplier code has been updated
            console.warn("The supplier code [", supplierCodeFromSample,"] not found where it must be", error)
          } else {
            // rethrow the error to get the built-in pop-in
            throw error;
          }
        }
      });
  }

  onSupplierChange(event: SelectionItem<SupplierDto>): void {
    this.mandatoryForm.get("supplier").setValue(event);
    this.selectedSupplier = event?.value;
    this.selectedSupplierChange.next(this.selectedSupplier);
  }

  /**
   * Update the meta datas which surround the entity and the business unit
   *
   * @param code the code of the sub entity to retrieve
   */
  private async updateEntityAndLinkedForm(sample: SampleDto): Promise<void> {
    if (this.selectedEntity?.code && this.selectedEntity.code === this.sample?.entity.identifier) {
      // Check if the selected entity is equal to the one from the sample to
      // avoid to request a entity which has already been retrieved
      await this.onEntityChange(NeqSampleMandatoryProperties.returnSelectionItem(this.selectedEntity, false));
      this.updateBusinessFormFromSample(sample);
      return;
    }
    this.subEntityService.getByCode(sample.entity.identifier)
      .subscribe(async (entity) => {
        try {
          const linkInfo: LinkInfoToEntityInfo = await this.onEntityChange(NeqSampleMandatoryProperties.returnSelectionItem(entity), true);
          if (!linkInfo.isThereLinkedBu) {
            this.updateBusinessFormFromSample(sample);
          }
          if (!linkInfo.isThereLinkedCustomer) {
            this.updateCustomerFormFromSample(sample);
          }
        } catch (e) {
          console.error('Error in updateEntityAndBusinessUnitForm : ', e);
        }
      });
  }

  /**
   * Update the entity form and all the linked datas
   *
   * @param event                    the event which trigs this method
   * @param forceAllLinkedDataUpdate at true if you want to force the update of data linked to entity
   * @returns true if the business unit has been updated AND there is a linked customer and bu to the entity
   */
  async onEntityChange(event: SelectionItem<EntityDto>, forceAllLinkedDataUpdate: boolean = true): Promise<LinkInfoToEntityInfo> {
    this.mandatoryForm.get('entity').setValue(event);
    this.selectedEntity = event?.value;
    if (this.selectedEntity?.logo) {
      this.entityLogo = this.domSanitizer.bypassSecurityTrustUrl(this.selectedEntity.logo);
    } else {
      this.entityLogo = undefined;
    }
    this.selectedEntityChange.next(this.selectedEntity);
    const linkInfo = new LinkInfoToEntityInfo();
    if (forceAllLinkedDataUpdate) {
      linkInfo.isThereLinkedBu = await this.updateBusinessUnitFromEntity(this.selectedEntity);
      linkInfo.isThereLinkedCustomer = await this.updateCustomerFromEntity(this.selectedEntity);
    } else {
      linkInfo.isThereLinkedBu = false;
      linkInfo.isThereLinkedCustomer = false;
    }
    return lastValueFrom(of(linkInfo));
  }

  updateBusinessUnitFromEntity(entity: EntityDto): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.retrieveBusinessUnitLinkToEntity(entity)
        .subscribe({
          next : linkedBu => {
            this.isThereLinkedBu = !!linkedBu;
            this.isThereLinkedBuChange.next(this.isThereLinkedBu);
            if (this.isThereLinkedBu) {
              this.onBusinessUnitChange(NeqSampleMandatoryProperties.returnSelectionItem(linkedBu));
            } else if (this.businessUnitFromMainEntity && (this.isThereLinkedBu || !this.selectedBusinessUnit)) {
              // The BU take the main one if :
              //    there is a main business unit
              //      AND
              //        (if previously there was not any linked BU (which means that the user has potentially select a BU)
              //        OR there was not any selectedBusinessUnit)
              this.onBusinessUnitChange(NeqSampleMandatoryProperties.returnSelectionItem(this.businessUnitFromMainEntity));
            }
            resolve(this.isThereLinkedBu);
          },
          error: error => {
            reject(new Error('Update BU failure.'));
            // Re-throw the error to get the built in pop-in
            throw error;
          }
        });
      });
  }

  /**
   * Retrieve the business unit linked to the entity if there is
   *
   * @param entity the entity from what the retrieved BU must be form
   * @returns the linked Business unit and null if there is not
   */
  retrieveBusinessUnitLinkToEntity(entity: EntityDto): Observable<BusinessUnitDto> {
    if (!entity?.customer?.identifier) {
      return of(null);
    }
    const buFilter = new BusinessUnitFilterDto();
    buFilter.inCustomerCodes = [entity.customer.identifier];
    buFilter.count = 1;
    return this.businessUnitService.search(buFilter)
      .pipe(
        map(pagedResult => {
          if (pagedResult.total === 1) {
            return pagedResult.data[0];
          } else {
            return null
          }
        })
      );
  }

  /**
   * Update the customer form accroding the business unit and the main selected entity
   *
   * @param entity the business unit from what the customer must be linked
   */
  updateCustomerFromEntity(entity: EntityDto): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.retrieveCustomerLinkToEntity(entity)
        .subscribe({
          next : linkedCustomer => {
            this.isThereLinkedCustomer = !!linkedCustomer;
            this.isThereLinkedCustomerChange.next(this.isThereLinkedCustomer);
            if (this.isThereLinkedCustomer) {
              this.onCustomerChange(NeqSampleMandatoryProperties.returnSelectionItem(linkedCustomer));
            } else if (this.customerFromMainEntity && (this.isThereLinkedCustomer || !this.selectedCustomer)) {
              // The Customer take the main one if :
              //    there is a main customer
              //      AND
              //        (if previously there was not any linked customer (which means that the user has potentially select a customer)
              //        OR there was not any selectedCustomer)
              this.onCustomerChange(NeqSampleMandatoryProperties.returnSelectionItem(this.customerFromMainEntity));
            }
            resolve(this.isThereLinkedCustomer);
          },
          error: error => {
            reject(new Error('Update customer failure.'));
            // Re-throw the error to get the built in pop-in
            throw error;
          }
        });
    })

  }
  /**
   * Retrieve the business unit linked to the entity if there is
   *
   * @param entity the entity from what the retrieved BU must be form
   * @returns the linked Business unit and null if there is not
   */
  retrieveCustomerLinkToEntity(entity: EntityDto): Observable<CustomerDto> {
    const customerCodeFromEntity = entity?.customer?.identifier;
    if (!customerCodeFromEntity) {
      return of(null);
    }
    const customerFilter = new CustomerFilterDto();
    customerFilter.codes = [customerCodeFromEntity];
    customerFilter.textMatchMode = 'STRICT';
    customerFilter.count = 1;
    return this.customerService.search(customerFilter)
      .pipe(
        map(pagedResult => {
          if (pagedResult.total === 1) {
            return pagedResult.data[0];
          } else {
            return null
          }
        })
      );
  }

  /**
   * Update the business unit form according sample data
   *
   * @param sample the data where the business unit must be from
   */
  updateBusinessFormFromSample(sample: SampleDto): void {
    const buCodeFromSample = sample?.originalBusinessUnit?.identifier;
    if (this.selectedBusinessUnit?.code && this.selectedBusinessUnit.code === buCodeFromSample) {
      // Check if the selected bu is equal to the one from the sample to
      // avoid to request a bu which has already been retrieved
      this.onBusinessUnitChange(NeqSampleMandatoryProperties.returnSelectionItem(this.selectedBusinessUnit));
    } else if (buCodeFromSample) {
      this.businessUnitService.getByCode(buCodeFromSample)
        .subscribe({
          next: bu => this.onBusinessUnitChange(NeqSampleMandatoryProperties.returnSelectionItem(bu)),
          error: (error: HttpErrorResponse) => {
            if (error?.status === HttpStatusCode.NotFound) {
              // The reasons that the bu code that appears in the sample does not exsits can be due to event
              // that happens after the creation or updates of sample like :
              // the bu has been removed or the bu code has been updated
              console.warn("The business unit code [", buCodeFromSample,"] not found where it must be", error);
            } else {
              // rethrow the error to get the built-in popin
              throw error;
            }
          }
        });
    }
  }

  /**
   * Handle the selection updates of business unit
   *
   * @param event the selection item
   */
  onBusinessUnitChange(event: SelectionItem<BusinessUnitDto>): void {
    this.mandatoryForm.get("businessUnit").setValue(event);
    this.selectedBusinessUnit = event?.value;
    this.selectedBusinessUnitChange.next(this.selectedBusinessUnit);
    // Reset the selector to update the list of customer to only get
    // the customer list linked to the selected bu only
    this.customerSelector?.reset(true, false);
  }

  /**
   * Update the customer form according sample data
   *
   * @param sample the data where the customer must be from
   */
  updateCustomerFormFromSample(sample: SampleDto): void {
    const customerCodeFromSample = sample?.customer?.urn?.identifier;
    if (this.selectedCustomer?.code && this.selectedCustomer.code === customerCodeFromSample) {
      // Check if the selected bu is equal to the one from the sample to
      // avoid to request a bu which has already been retrieved
      this.onCustomerChange(NeqSampleMandatoryProperties.returnSelectionItem(this.selectedCustomer));
    } else if (customerCodeFromSample) {
      this.customerService.getByCode(customerCodeFromSample)
        .subscribe({
          next: customer => this.onCustomerChange(NeqSampleMandatoryProperties.returnSelectionItem(customer)),
          error: (error: HttpErrorResponse) => {
            if (error?.status === HttpStatusCode.NotFound) {
              // The reasons that the customer code that appears in the sample does not exsits can be due to event
              // that happens after the creation or updates of sample like :
              // the customer has been removed or the customer code has been updated
              console.warn("The customer code [", customerCodeFromSample,"] not found where it must be", error);
            } else {
              // Rethrow the error to get the built-in popin
              throw error;
            }
          }
        });
    }
  }

  /**
   * Handle the selection updates of customer
   *
   * @param event the selection item
   */
  onCustomerChange(event: SelectionItem<CustomerDto>): void {
    this.mandatoryForm.get("customer").setValue(event);
    this.selectedCustomer = event?.value;
    this.selectedCustomerChange.next(this.selectedCustomer);
  }

  callStaticCodeAndLabel(originalValue: { code?: string, label?: string, name?: string }, withCode: boolean = true): string {
    return NeqSampleMandatoryProperties.returnCodeAndLabel(originalValue, withCode);
  }

  /**
   * Return the SelectionItem from the original value
   *
   * @param originalValue the value which will be at the origin of the form value
   * @param withCode      at true if the label of the SelectionItem must contains the code
   * @returns the SelectionItem
   */
  private static returnSelectionItem(originalValue: { code?: string, label?: string, name?: string }, withCode: boolean = true): SelectionItem<any> {
    return originalValue ? new SelectionItem(originalValue, this.returnCodeAndLabel(originalValue, withCode)) : null;
  }

  /**
   * Return code and label of object
   *
   * @param originalValue the value which will be at the origin of the form value
   * @param withCode      at true if the label of the SelectionItem must contains the code
   * @returns the SelectionItem
   */
  private static returnCodeAndLabel(originalValue: { code?: string, label?: string, name?: string }, withCode: boolean = true): string {
    if (!originalValue) {
      return '';
    }
    const code = withCode && originalValue ? `${originalValue.code} - ` : '';
    return `${ code } ${ originalValue.label ?? originalValue.name }`;
  }

  /**
   * Return the SelectionItem from an URN
   *
   * @param urn the URN to convert it as a SelectionItem
   * @returns the corresponding SelectionItem
   */
  private static returnSelectionItemFromUrn(urn: URN): SelectionItem<any> {
    return urn?.identifier ?
            // Set the value of SelectionItem as { code: string } to be sure to be able to reformulate the URN value
            // when returning the sample from the form
            new SelectionItem({ code: urn.identifier }, urn.identifier)
            : null;
  }

  /**
   * Return the sample value which correspond to the form
   *
   * @param mandatoryForm the form which has sample attributes value
   */
  public static returnSampleFromForm(mandatoryForm: FormGroup): SampleDto {
    const sampleFromForm = new SampleDto();
    sampleFromForm.code = mandatoryForm.get("code").value;
    sampleFromForm.label = mandatoryForm.get("label").value;
    sampleFromForm.reference = this.returnUrnFromSelectionItemForm(mandatoryForm, "reference");
    sampleFromForm.entity = this.returnUrnFromSelectionItemForm(mandatoryForm, "entity");
    sampleFromForm.supplierUrn = this.returnUrnFromSelectionItemForm(mandatoryForm, "supplier");
    sampleFromForm.validationDate = mandatoryForm.get("validationDate").value;
    sampleFromForm.species = (mandatoryForm.get("species").value as SelectionItem<SpeciesDto>)?.value;
    sampleFromForm.analysisOrigin  = (mandatoryForm.get("analysisOrigin").value as SelectionItem<AnalysisOriginDto>)?.value;
    sampleFromForm.customer  = new SampleCustomerDto();
    sampleFromForm.customer.urn = this.returnUrnFromSelectionItemForm(mandatoryForm, "customer");
    const selectedRequester: SelectionItem<UserDto> = mandatoryForm.get("requester").value;
    sampleFromForm.requester = selectedRequester.label;
    const user: UserDto = selectedRequester.value;
    // Trim it to do not store useless space which can be introduced by empty family name setted at the init of the form
    sampleFromForm.technicianName = `${user.givenName} ${user.familyName}`.trim();
    sampleFromForm.farm = new SampleFarmDto();
    sampleFromForm.farm.label = mandatoryForm.get("farmLabel").value;
    sampleFromForm.originalBusinessUnit = this.returnUrnFromSelectionItemForm(mandatoryForm, "businessUnit")
    sampleFromForm.excludeFromTheStats = mandatoryForm.get("excludeFromTheStats").value;
    return sampleFromForm;
  }

  /**
   * Return the URN to set in the JSON object to fill from a form which has
   * as a value type a SelectionItem
   *
   * @param mandatoryForm the form from what the value will be taken
   * @param formPath      the path in the form that contains the code to add in the URN
   * @returns the corresponding URN
   */
  private static returnUrnFromSelectionItemForm(mandatoryForm: FormGroup, formPath: string): URN {
    const selectionItemValue: SelectionItem<any> = mandatoryForm.get(formPath).value;
    const urn = new URN();
    urn.identifier = selectionItemValue.value.code;
    return urn;
  }

  /**
   * Search allowed reference
   *
   * @param searchTerm the search term to look for if there is
   * @param page       the number of the page to search
   * @returns the array of SelectionItem which has been found
   */
  searchReference = (searchTerm: string, page: number): Promise<SelectionItem<ReferenceDto>[]> => {
    const filters = new ReferenceFilterDto();

    if (searchTerm) {
      filters.label = searchTerm;
      filters.code = searchTerm;
    }
    filters.page = page;
    filters.matchAny = true;
    return lastValueFrom(
      this.entityService.referencesFetch(this.userService.getEntity(), filters).pipe(
        map(e =>
          e.data.map(r => NeqSampleMandatoryProperties.returnSelectionItem(r))
        )
      )
    );
  };

  /**
   * Search allowed business unit
   *
   * @param searchTerm the search term to look for if there is
   * @param page       the number of the page to search
   * @returns the array of SelectionItem which has been found
   */
  searchBusinessUnit = (searchTerm: string, page: number): Promise<SelectionItem<BusinessUnitDto>[]> => {
    const filters = new BusinessUnitFilterDto();
    if (searchTerm) {
      filters.label = searchTerm;
      filters.codes = [searchTerm];
    }
    filters.page = page;
    filters.matchAny = true;
    return lastValueFrom(
      this.businessUnitService.searchByEntityCode(this.userService.getEntity(), filters).pipe(
        map(e =>
          e.data.map(r => NeqSampleMandatoryProperties.returnSelectionItem(r))
        )
      )
    );
  };

  /**
   * Search allowed customer
   *
   * @param searchTerm the search term to look for if there is
   * @param page       the number of the page to search
   * @returns the array of SelectionItem which has been found
   */
  searchCustomer = (searchTerm: string, page: number): Promise<SelectionItem<CustomerDto>[]> => {
    const filters = new CustomerFilterDto();
    if (searchTerm) {
      filters.label = searchTerm;
      filters.codes = [searchTerm];
    }
    if (this.selectedBusinessUnit) {
      filters.inBusinessUnitIds = [this.selectedBusinessUnit.id];
    }
    filters.page = page;
    filters.matchAny = true;
    return lastValueFrom(
      this.customerService.searchByEntityCode(this.userService.getEntity(), filters).pipe(
        map(e =>
          e.data.map(r => NeqSampleMandatoryProperties.returnSelectionItem(r))
        )
      )
    );
  };

  /**
   * Search the entity linked to the one selected by the user
   *
   * @param searchTerm the search term to look for if there is
   * @param page       the number of the page to search
   * @returns the array of SelectionItem which has been found
   */
  searchEntity = (searchTerm: string, page: number): Promise<SelectionItem<EntityDto>[]> => {
    const filters = new EntityFilterDto();

    if (searchTerm) {
      filters.matchAny = true;
      filters.codes = [searchTerm];
      filters.name = searchTerm;
    }
    filters.allowedFor = this.userService.getEntityDto().id;
    filters.page = page;

    return lastValueFrom(
      this.subEntityService.fetch(filters)
        .pipe(
          map(e =>
            e.data.map(entity => NeqSampleMandatoryProperties.returnSelectionItem(entity))
          )
        )
      );
  }

  /**
   * Search the analysis
   *
   * @param searchTerm the search term to look for if there is
   * @param page       the number of the page to search
   * @returns the array of SelectionItem which has been found
   */
  searchAnalysis = (searchTerm: string, page: number): Promise<SelectionItem<AnalysisOriginDto>[]> => {
    const filters = new AnalysisOriginFilterDto();

    if (searchTerm) {
      filters.matchAny = true;
      filters.label = searchTerm;
    }
    filters.excludeForNutrientOnly = true;
    filters.sort = [ { field: "label", asc: true }]
    filters.page = page;

    return lastValueFrom(
      this.anaylysisService.search(filters)
        .pipe(
          map(e =>
            e.data.map(item => NeqSampleMandatoryProperties.returnSelectionItem(item, false))
          )
        )
      );
  }

  /**
   * Search the supplier
   *
   * @param searchTerm the search term to look for if there is
   * @param page       the number of the page to search
   * @returns the array of SelectionItem which has been found
   */
  searchSupplier = (searchTerm: string, page: number): Promise<SelectionItem<SupplierDto>[]> => {
    const filters = new SupplierFilterDto();

    if (searchTerm) {
      filters.matchAny = true;
      filters.codes = [searchTerm];
      filters.label = searchTerm;
    }
    filters.page = page;

    return lastValueFrom(
      this.supplierService.search(filters)
        .pipe(
          map(e =>
            e.data.map(item => NeqSampleMandatoryProperties.returnSelectionItem(item))
          )
        )
      );
  }

  /**
   * Search the species
   *
   * @param searchTerm the search term to look for if there is
   * @param page       the number of the page to search
   * @returns the array of SelectionItem which has been found
   */
  searchSpecies = (searchTerm: string, page: number): Promise<SelectionItem<SpeciesDto>[]> => {
    const filters = new SpeciesFilterDto();

    if (searchTerm) {
      filters.matchAny = true;
      filters.label = searchTerm;
    }
    filters.sort = [ { field: "label", asc: true }]
    filters.page = page;

    return lastValueFrom(
      this.speciesService.search(filters)
        .pipe(
          map(e =>
            e.data.map(item => NeqSampleMandatoryProperties.returnSelectionItem(item, false))
          ),
          tap(items => this.fillDefaultSpeciesIfNeeded(items))
        )
      );
  }

  fillDefaultSpeciesIfNeeded(speciesItems: SelectionItem<SpeciesDto>[]): void {
    if (speciesItems.length > 0) {
      const speciesForm = this.mandatoryForm.get('species');
      if (speciesForm.pristine && !speciesForm.value) {
        // If not setted by user and has not a value yet
        speciesForm.setValue(speciesItems[0]);
      }
    }
  }

  /**
   * Search the possible email of requester
   *
   * @param searchTerm the search term to look for if there is
   * @param page       the number of the page to search
   * @returns the array of SelectionItem which has been found
   */
  searchRequesters = (searchTerm: string, page: number): Promise<SelectionItem<UserDto>[]> => {
    const filters = new UserFilterDto();
    if (searchTerm) {
      filters.email = searchTerm;
    }
    filters.matchAny = false;
    filters.active = true;
    filters.entityId = this.userService.getEntityDto().id;
    filters.withAssociatedEntities = true;
    filters.sort = [ { field: "email", asc: true }]
    filters.page = page;

    return lastValueFrom(
      this.userRestService.fetch(filters)
        .pipe(
          map(e =>
            e.data.map(item => new SelectionItem<UserDto>(item, item.email))
          )
        )
      );
  }

  openAdvancedReferenceSearch() {
    const dialog = this.dialogMat.open(NeqAdvancedReferencesSearchPopinComponent);
    dialog.afterClosed().subscribe((data: ReferenceDto) => {
      if (data) {
       this.onReferenceChange(NeqSampleMandatoryProperties.returnSelectionItem(data));
      }
    });
  }

  isFormInvalidAndTouched(pathName: string): boolean {
    const form = this.mandatoryForm.get(pathName);
    return form.invalid && form.touched;
  }

  returnDateFormat() {
    return this.userService.returnUserDateFormat();
  }

  save() {
    this.saveClick.next();
  }
}
